import type {
  ConnectbaseNoticeStatus,
  ConnectbaseTaskKey,
  ProjectConnectbaseTasksResponse,
} from '~/api/projectConnectbaseTasks';

export type ConnectbaseStatus = Partial<Record<ConnectbaseTaskKey, ConnectbaseNoticeStatus>>;

const TASKS: ConnectbaseTaskKey[] = ['agencies', 'fiberLitBuildings'];
const STATUSES: ConnectbaseNoticeStatus[] = ['retrying', 'failed'];
const AGENCIES_STILL_LOADING = ['pending', 'inProgress', 'pendingExternalRetry'];

/** Why a ConnectBase-fed layer is unavailable, shown in its layer tooltip and announced to screen readers. */
export function connectbaseLayerStatusText(layerName: string, status: ConnectbaseNoticeStatus): string {
  return status === 'retrying'
    ? `${layerName} is still loading from our data provider, we are retrying.`
    : `${layerName} wasn’t able to be loaded from our data provider.`;
}

/** Keeps only known tasks and open statuses, since the client and api deploy separately. */
export function toConnectbaseStatus(response: ProjectConnectbaseTasksResponse | undefined): ConnectbaseStatus {
  const status: ConnectbaseStatus = {};
  for (const row of response?.tasks ?? []) {
    if (TASKS.includes(row.task) && STATUSES.includes(row.status)) status[row.task] = row.status;
  }
  return status;
}

/**
 * Which agencies notice the Utilities tab shows. A `failed` agencies status without a failed ConnectBase row came
 * from something else (e.g. project creation), so it keeps the generic error.
 */
export function resolveAgenciesNotice(
  agenciesTaskStatus: string | undefined,
  connectbase: ConnectbaseNoticeStatus | undefined,
): ConnectbaseNoticeStatus | null {
  if (agenciesTaskStatus === 'failed') return connectbase === 'failed' ? 'failed' : null;
  if (agenciesTaskStatus && AGENCIES_STILL_LOADING.includes(agenciesTaskStatus) && connectbase === 'retrying') {
    return 'retrying';
  }
  return null;
}

/**
 * What the Utilities tab renders; the retrying notice replaces both the poll spinner and its timeout error. An
 * error waits on an in-flight status request so the generic message doesn't flash before the notice.
 */
export function pickUtilitiesBody(args: {
  isLoading: boolean;
  isError: boolean;
  notice: ConnectbaseNoticeStatus | null;
  statusPending: boolean;
}): 'notice' | 'spinner' | 'error' | 'content' {
  const { isLoading, isError, notice, statusPending } = args;
  if (notice === 'retrying' && (isLoading || isError)) return 'notice';
  if (notice === 'failed' && isError) return 'notice';
  if (isLoading) return 'spinner';
  if (isError) return statusPending ? 'spinner' : 'error';
  return 'content';
}
